import type { KeyboardEvent } from 'react';
import { Editor, isMacOS, Node } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { NodeView } from '@tiptap/pm/view';

import { TEMPLATE_FIELD_DROPDOWN_NAME } from '../../../doc-engine/templateFields';
import { stripZeroWidthSpaces } from '../../../doc-engine/text';
import { isFieldInCard } from '../formField/formFieldHelpers';
import { setAttrsAtPos } from '../../extensions/listUtils';

export type TFieldSettings = {
  label: string | null;
  description: string | null;
  required: boolean;
};

// One empty value: a cleared or blank label or description is null, as one never set is.
export const toTextSetting = (value: string | null): string | null => (value?.trim() ? value : null);

const FIELD_SETTINGS_ATTRIBUTES = {
  label: {
    default: null,
    parseHTML: (element: HTMLElement) => toTextSetting(element.getAttribute('data-label')),
    renderHTML: ({ label }: Record<string, unknown>) => (label ? { 'data-label': label } : {}),
  },
  description: {
    default: null,
    parseHTML: (element: HTMLElement) => toTextSetting(element.getAttribute('data-description')),
    renderHTML: ({ description }: Record<string, unknown>) => (description ? { 'data-description': description } : {}),
  },
  required: {
    default: false,
    parseHTML: (element: HTMLElement) => element.hasAttribute('data-required'),
    renderHTML: ({ required }: Record<string, unknown>) => (required ? { 'data-required': '' } : {}),
  },
};

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/documents/src/hasUnlabelledTemplateField.ts
export const isUnlabelled = (label: string | null | undefined): boolean => !label?.trim();

/** No answer: a Dropdown without a selected value, a text field with nothing beyond the caret's ZWSP. */
const isFieldEmpty = (field: PMNode): boolean => {
  if (field.type.name === TEMPLATE_FIELD_DROPDOWN_NAME) return !field.attrs.selectedValue;
  let hasAnswer = false;
  field.descendants((node) => {
    hasAnswer ||= node.isText ? !!stripZeroWidthSpaces(node.text!).trim() : node.isLeaf;
    return !hasAnswer;
  });
  return !hasAnswer;
};

const setAttribute = (dom: HTMLElement, name: string, value: string | null) =>
  value === null ? dom.removeAttribute(name) : dom.setAttribute(name, value);

/** Writes a field's settings onto its node view for the content styles; CSS cannot tell a blank label from a label. */
const syncFieldSettings = (dom: HTMLElement, field: PMNode, isInCard: boolean): void => {
  const { label, description, required } = field.attrs as TFieldSettings;
  const hasLabel = !isUnlabelled(label);
  setAttribute(dom, 'data-label', hasLabel ? label : null);
  setAttribute(dom, 'data-unlabelled', hasLabel ? null : '');
  setAttribute(dom, 'data-required', required ? '' : null);
  setAttribute(dom, 'data-empty', isFieldEmpty(field) ? '' : null);
  // A card shows the description under its label; elsewhere the field's tooltip reads it from here.
  setAttribute(dom, 'data-description', isInCard ? null : description || null);
};

/** Label, Description and Required on an existing field, which keeps its own node view and behavior. */
export const withFieldSettings = (field: Node): Node =>
  field.extend({
    addAttributes() {
      return { ...this.parent?.(), ...FIELD_SETTINGS_ATTRIBUTES };
    },

    addNodeView() {
      const fieldNodeView = this.parent?.();
      if (!fieldNodeView) return null;

      return (props) => {
        const view = fieldNodeView(props) as NodeView;
        const dom = view.dom as HTMLElement;
        const isInCard = () => {
          const pos = props.getPos();
          return pos !== undefined && isFieldInCard(props.editor.state.doc, pos);
        };
        syncFieldSettings(dom, props.node, isInCard());

        return {
          ...view,
          update: (node, decorations, innerDecorations) => {
            const isUpdated = view.update?.(node, decorations, innerDecorations) ?? false;
            if (isUpdated) syncFieldSettings(dom, node, isInCard());
            return isUpdated;
          },
        };
      };
    },
  });

/** Writes settings onto the field at `fieldPos`, keeping its ids, marks and content. */
export const setFieldSettings = (editor: Editor, fieldPos: number, patch: Partial<TFieldSettings>): void => {
  const { tr } = editor.state;
  const field = tr.doc.nodeAt(fieldPos);
  const settings = Object.fromEntries(
    Object.entries(patch).map(([key, value]) => [key, typeof value === 'string' ? toTextSetting(value) : value])
  );
  if (field && setAttrsAtPos(tr, fieldPos, field.type, settings)) editor.view.dispatch(tr);
};

/** Settings write to the editor's history, so their inputs send undo and redo there. */
export const handleSettingsHistoryKeys = (editor: Editor, event: KeyboardEvent): void => {
  const key = event.key.toLowerCase();
  if (!(isMacOS() ? event.metaKey : event.ctrlKey) || (key !== 'z' && key !== 'y')) return;
  event.preventDefault();
  if (key === 'y' || event.shiftKey) editor.commands.redo();
  else editor.commands.undo();
};
